import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useWorkspace } from '@/contexts/WorkspaceContext';
import { queryKeys } from '@/lib/queryKeys';
import type { AgentDefinition } from '@/services/agents';
import {
    fetchWorkspaceAgentOverrides,
    getGlobalAgents,
    mergeAgentConfigs,
    mergeWorkspaceAgentOverrides,
    updateWorkspaceAgentEnabled,
} from '@/services/agents';

/**
 * Hook to get agents configuration with workspace-specific overrides applied.
 *
 * - Global agents come from schema defaults (synchronous, baked in code)
 * - Workspace-specific overrides from config.client_configs (async DB query), keyed by
 *   the workspace's config key. A workspace under a path resolves its host's toggles
 *   with its own laid over, as the runtime does — but a toggle writes only its OWN row,
 *   so it keeps inheriting host changes for agents it hasn't overridden.
 * - Exposes toggleAgent() for staff to enable/disable agents per domain
 */
export function useAgentsConfig() {
    const { selectedDomain, selectedConfigKey, loading: workspaceLoading } = useWorkspace();
    const queryClient = useQueryClient();

    const globalAgents = getGlobalAgents();

    const workspaceConfigQuery = useQuery({
        queryKey: queryKeys.agents.workspaceConfig(selectedConfigKey),
        queryFn: async () => {
            if (selectedConfigKey === selectedDomain) {
                const own = await fetchWorkspaceAgentOverrides(selectedConfigKey);
                return { resolved: own, own };
            }
            const [host, own] = await Promise.all([
                fetchWorkspaceAgentOverrides(selectedDomain),
                fetchWorkspaceAgentOverrides(selectedConfigKey),
            ]);
            return { resolved: mergeWorkspaceAgentOverrides(host, own), own };
        },
        enabled: !!selectedConfigKey,
        staleTime: 5 * 60 * 1000,
    });

    // Display shows the resolved (host+own) state; the toggle writes only the own row.
    const agents: AgentDefinition[] = mergeAgentConfigs(
        globalAgents,
        workspaceConfigQuery.data?.resolved ?? null
    );

    const ownOverrides = workspaceConfigQuery.data?.own?.agents ?? {};

    const toggleMutation = useMutation({
        mutationFn: ({ agentId, enabled }: { agentId: string; enabled: boolean }) =>
            updateWorkspaceAgentEnabled(selectedConfigKey, agentId, enabled, ownOverrides),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.agents.workspaceConfig(selectedConfigKey),
            });
        },
    });

    const toggleAgent = (agentId: string, enabled: boolean) => {
        toggleMutation.mutate({ agentId, enabled });
    };

    const isLoading = workspaceLoading || workspaceConfigQuery.isLoading;
    const error = workspaceConfigQuery.error;

    return {
        agents,
        isLoading,
        error,
        selectedDomain,
        toggleAgent,
        isToggling: toggleMutation.isPending,
        togglingAgentId: toggleMutation.variables?.agentId ?? null,
    };
}
